import { FC, useState } from "react"
import { ArrowRightLeft, AlertTriangle } from "lucide-react"
import { Brand, LoanOfficer } from "../../../../types"
import LoanOfficerAutocomplete from "../../loan-officer-autocomplete"
import { PrimaryBtn } from "../../buttons"
import Dialog from "../../dialog"
import loTransfer from "../../../../api/lo-transfer"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"

interface Props {
  brands: Brand[]
}

const LoTransferAssets: FC<Props> = ({ brands }) => {
  const dispatch = useAppDispatch()
  const [brandId, setBrandId] = useState<string>("")
  const [brandName, setBrandName] = useState<string>("")
  const [fromLoid, setFromLoid] = useState<string>("")
  const [toLoid, setToLoid] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const canTransfer = brandId && fromLoid && toLoid && fromLoid !== toLoid

  const handleTransfer = async () => {
    setShowConfirm(false)
    setIsLoading(true)

    try {
      await loTransfer.transferAssets({
        from_loid: parseInt(fromLoid, 10),
        to_loid: parseInt(toLoid, 10)
      })

      dispatch(
        pushToast({
          msg: "Assets transferred successfully",
          theme: "success",
          timeout: 3000
        })
      )

      // Reset form
      setBrandId("")
      setBrandName("")
      setFromLoid("")
      setToLoid("")
    } catch (error: any) {
      dispatch(
        pushToast({
          msg: error?.message || "Failed to transfer assets",
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleFromLoSelect = (lo: LoanOfficer) => {
    setBrandId(lo.bank_nid)
    // Look up brand name from brands list since API doesn't always return it
    const brand = brands.find(b => b.id === lo.bank_nid)
    setBrandName(brand?.name ?? lo.brand_name ?? "")
    setToLoid("")
  }

  const handleFromLoChange = (val: string) => {
    setFromLoid(val)
    if (!val) {
      setBrandId("")
      setBrandName("")
      setToLoid("")
    }
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg">
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <ArrowRightLeft className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              LO Asset Transfer
            </h3>
            <p className="text-sm text-gray-500">
              Transfer all assets from one loan officer to another
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">This action transfers all assets</p>
            <p className="mt-1 text-amber-700">
              Leads, SMS threads, integrations, rateflows, API keys, and pricing
              sets will be moved. Both loan officers must be in the same brand.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Loan Officer:
            </label>
            <LoanOfficerAutocomplete
              selected={fromLoid}
              label=""
              direction="vertical"
              showBrandName
              showSearchToggle
              onChange={handleFromLoChange}
              onSelectLo={handleFromLoSelect}
            />
          </div>

          {brandId && (
            <>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Brand:</span> {brandName}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Loan Officer:
                </label>
                <LoanOfficerAutocomplete
                  searchParams={{ brand_id: brandId }}
                  selected={toLoid}
                  label=""
                  direction="vertical"
                  excludeIds={fromLoid ? [fromLoid] : []}
                  onChange={setToLoid}
                />
              </div>
            </>
          )}
        </div>

        <div className="pt-2">
          <PrimaryBtn
            onClick={() => setShowConfirm(true)}
            disabled={!canTransfer || isLoading}
            className="w-full justify-center"
          >
            {isLoading ? "Transferring..." : "Transfer Assets"}
          </PrimaryBtn>
        </div>
      </div>

      <Dialog
        open={showConfirm}
        title="Confirm Asset Transfer"
        body="Are you sure you want to transfer all assets from the source loan officer to the target loan officer? This action cannot be undone."
        okLabel="Yes, Transfer Assets"
        cancelLabel="Cancel"
        onOk={handleTransfer}
        onCancel={() => setShowConfirm(false)}
        onClose={() => setShowConfirm(false)}
        theme="warning"
      />
    </div>
  )
}

export default LoTransferAssets
